import React, { useState, useEffect } from "react";
import "../../styles/clinicsetup.css";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const ViewAllVetsModal = ({ open, onClose, veterinarians = [], clinicId }) => {
  const [clinicMembers, setClinicMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  // Fetch clinic members when modal opens
  useEffect(() => {
    if (open && clinicId) {
      fetchClinicMembers();
    }
  }, [open, clinicId]);

  const fetchClinicMembers = async (page = 1) => {
    if (!clinicId) {
      setError("No clinic ID provided");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(
        `${API_BASE_URL}/api/v1/clinic-members?clinic_id=${clinicId}&page=${page}`,
        {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch clinic members");
      }

      const result = await response.json();
      if (result.status === "success") {
        setClinicMembers(result.data || []);
        setPagination(result.pagination);
        setCurrentPage(page);
      } else {
        const errorMessage = result.message || "Failed to fetch clinic members";
        setError(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching clinic members:", error);
      setError("Failed to fetch clinic members");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination?.last_page) {
      fetchClinicMembers(page);
    }
  };

  if (!open) return null;

  // Use clinic members data if available, otherwise fall back to veterinarians prop
  const vets = clinicMembers.length > 0 
    ? clinicMembers.map(member => ({
        id: member.id,
        name: member.doctor ? `${member.doctor.first_name} ${member.doctor.last_name}` : "Unknown Doctor",
        schedule: `Status: ${member.status.charAt(0).toUpperCase() + member.status.slice(1)}`,
        img: member.doctor?.profile_image_url || "/images/doctor.png",
        isOwner: member.is_owner,
        status: member.status,
        email: member.doctor?.email
      }))
    : Array(8)
        .fill(0)
        .map((_, i) => veterinarians[i % veterinarians.length]);

  return (
    <div className="viewallvets-modal-overlay">
      <div className="viewallvets-modal">
        <h2 className="viewallvets-title">Current Veterinarians</h2>
        <button className="viewallvets-close" onClick={onClose}>
          &times;
        </button>
        
        {isLoading ? (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center', 
            color: '#666',
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            Loading veterinarians...
          </div>
        ) : error ? (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center', 
            color: '#e74c3c',
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {error}
          </div>
        ) : (
          <>
            <div className="viewallvets-grid">
              {vets.map((vet, idx) => (
                <div className="viewallvets-card" key={vet.id || idx}>
                  <img
                    src={vet.img}
                    alt={vet.name}
                    className="viewallvets-avatar"
                    onError={(e) => (e.target.style.display = "none")}
                  />
                  <div className="viewallvets-name">{vet.name}</div>
                  <div className="viewallvets-specialty">{vet.specialty}</div>
                  <div className="viewallvets-schedule">{vet.schedule}</div>
                  {vet.isOwner && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#f39c12', 
                      fontWeight: 'bold',
                      marginBottom: '8px'
                    }}>
                      Owner
                    </div>
                  )}
                  <div className="viewallvets-actions">
                    <a href="#" className="viewallvets-profile">
                      View Profile
                    </a>
                    {!vet.isOwner && (
                      <button className="viewallvets-remove">Remove</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {pagination && pagination.last_page > 1 && (
              <div className="viewallvets-pagination">
                <button 
                  className="viewallvets-page-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &#60;
                </button>
                
                {/* Show page numbers */}
                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                  let pageNum;
                  if (pagination.last_page <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.last_page - 2) {
                    pageNum = pagination.last_page - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <span 
                      key={pageNum}
                      className={`viewallvets-page-num ${currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                      style={{ cursor: 'pointer' }}
                    >
                      {pageNum}
                    </span>
                  );
                })}
                
                {pagination.last_page > 5 && currentPage < pagination.last_page - 2 && (
                  <span className="viewallvets-page-dots">...</span>
                )}
                
                {pagination.last_page > 5 && currentPage < pagination.last_page - 2 && (
                  <span 
                    className="viewallvets-page-num"
                    onClick={() => handlePageChange(pagination.last_page)}
                    style={{ cursor: 'pointer' }}
                  >
                    {pagination.last_page}
                  </span>
                )}
                
                <button 
                  className="viewallvets-page-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === pagination.last_page}
                >
                  &#62;
                </button>
              </div>
            )}
          </>
        )}
        
        <button className="viewallvets-back-btn" onClick={onClose}>
          Back to clinic
        </button>
      </div>
    </div>
  );
};

export default ViewAllVetsModal;
